import React, { createContext, useContext, useState, useEffect } from 'react';
import { requestHelpers } from '../services/api';
import { hashString } from '../utils/security';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on component mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);

          // Verify token is still valid by fetching current user profile
          const response = await requestHelpers.get('/auth/profile');

          setUser(response.user);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          clearAuthData();
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const saveAuthData = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  // Register new user
  const register = async (userData) => {
    try {
      setLoading(true);
      // Hash password before sending to prevent visibility in Network tab
      const preparedData = { ...userData };
      if (preparedData.password) {
        preparedData.password = await hashString(preparedData.password);
      }
      const response = await requestHelpers.post('/auth/register', preparedData);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async (userId, otp) => {
    try {
      setLoading(true);
      const response = await requestHelpers.post('/auth/verify-otp', { userId, otp });

      if (response.success && response.token) {
        saveAuthData(response.token, response.user);
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async (userId) => {
    try {
      const response = await requestHelpers.post('/auth/resend-otp', { userId });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Login user
  const login = async (email, password, rememberMe = false) => {
    try {
      setLoading(true);
      // Hash password before sending to prevent visibility in Network tab
      const hashedPassword = await hashString(password);
      const response = await requestHelpers.post('/auth/login', { email, password: hashedPassword });

      if (response.success && response.token) {
        saveAuthData(response.token, response.user);

        // Handle remember me functionality
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('rememberedEmail');
        }
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Google login
  const googleLogin = async (tokenId) => {
    try {
      setLoading(true);
      const response = await requestHelpers.post('/auth/google-login', { tokenId });

      if (response.success && response.token) {
        saveAuthData(response.token, response.user);
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };


  // Logout user
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate session on server
      await requestHelpers.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API fails
    } finally {
      clearAuthData();
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      const response = await requestHelpers.post('/auth/forgot-password', { email });
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (token, password) => {
    try {
      setLoading(true);
      const hashedPassword = await hashString(password);
      const response = await requestHelpers.post('/auth/reset-password', { token, password: hashedPassword });
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await requestHelpers.put('/auth/profile', profileData);

      if (response.success) {
        const updatedUser = { ...user, ...response.user };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Change password
  const changePassword = async (passwordData) => {
    try {
      const preparedData = { ...passwordData };
      if (preparedData.currentPassword) preparedData.currentPassword = await hashString(preparedData.currentPassword);
      if (preparedData.newPassword) preparedData.newPassword = await hashString(preparedData.newPassword);
      
      const response = await requestHelpers.put('/auth/change-password', preparedData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get user profile (refresh current user data)
  const refreshProfile = async () => {
    try {
      const response = await requestHelpers.get('/auth/profile');

      if (response.success) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Switch active WhatsApp account
  const switchWhatsAppAccount = async (whatsappNumber) => {
    try {
      const response = await requestHelpers.put('/user/switch-whatsapp', { whatsappNumber });

      if (response.success) {
        // Refresh profile to get the updated activeWhatsAppNumber and legacy fields
        await refreshProfile();
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Check if user has specific feature enabled
  const hasFeature = (feature) => {
    return user?.enabledFeatures?.includes(feature) || false;
  };

  // Get remembered email for login
  const getRememberedEmail = () => {
    const rememberMe = localStorage.getItem('rememberMe');
    const rememberedEmail = localStorage.getItem('rememberedEmail');

    return rememberMe === 'true' ? rememberedEmail : '';
  };

  // Auth context value
  const value = {
    // State
    user,
    loading,
    isAuthenticated,

    // Actions
    register,
    login,
    googleLogin,
    logout,
    verifyOTP,
    resendOTP,
    updateProfile,
    changePassword,
    refreshProfile,
    switchWhatsAppAccount,
    forgotPassword,
    resetPassword,

    // Utils
    hasFeature,
    getRememberedEmail,
    clearAuthData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};